// --------------------------------------------------------------
// Created On: 2025-04-29
// Author: Hannah Vaughan
//
// Last Modified: 2026-08-10
// Modified By: Hannah Vaughan
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useMemo, useState } from "react";
import styles from "./AssetManagementWizard.module.scss";
import Spinner from "../Spinner/Spinner";
import Modal from "../Modal/Modal";
import ModalHeader from "../ModalHeader/ModalHeader";
import ModalBody from "../ModalBody/ModalBody";
import ModalFooter from "../ModalFooter/ModalFooter";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import Error from "../Error/Error";
import Warning from "../Warning/Warning";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import SaveChangesModal from "../SaveChangesModal/SaveChangesModal";
import DetailsPage from "./DetailsPage/DetailsPage";
import ProductPage from "./ProductPage/ProductPage";
import ModbusPage from "./ModbusPage/ModbusPage";
import AnalogInputPage from "./AnalogInputPage/AnalogInputPage";
import DigitalInputPage from "./DigitalInputPage/DigitalInputPage";
import DigitalOutputPage from "./DigitalOutputPage/DigitalOutputPage";
import DataPage from "./DataPage/DataPage";
import useApi from "../../hooks/useApi";
import userHasPermission from "../../utilities/userHasPermission";
import apiRequest from "../../utilities/api/apiRequest";
import getApiError from "../../utilities/api/getApiError";
import deepCopy from "../../utilities/deepCopy";
import formatDateLocal from "../../utilities/time/formatDateLocal";
import useReduxSelector from "../../hooks/useReduxSelector";
import { ASSET_TYPES } from "../../constants/reducerActions";
import { ANALOG_INPUTS, DIGITAL_INPUTS } from "../../constants/attributes";
import { GOOGLE_MAPS_DEFAULT_LAT, GOOGLE_MAPS_DEFAULT_LNG } from "../../constants/googleMaps";
import {
  CREATE_ASSETS_PERMISSION,
  DELETE_ASSETS_PERMISSION,
  UPDATE_ASSETS_PERMISSION,
} from "../../constants/permissions";
import {
  API,
  DATA_COLLECTION_MONITORING_DEVICE,
  DATA_COLLECTION_NONE,
  DATA_COLLECTION_OTHER_ASSET,
  MAX_ASSET_FIELDS_LENGTH,
  MAX_ASSET_NAME_LENGTH,
  MIN_ASSET_FIELDS_LENGTH,
  MIN_ASSET_NAME_LENGTH,
  PULSE_DEVICE_TYPE,
  SCADA_RTU_DEVICE_TYPE,
} from "../../constants/miscellaneous";
import { GetAssetConfigurationResponseBody } from "src/types/api/GetAssetConfigurationResponseBody";

// Wizard for creating, updating, and deleting assets.
export default function AssetManagementWizard(props: Props): Component {
  const initialAsset: AssetConfiguration = {
    assetId: 0,
    name: "",
    nickname: "",
    assetTemplateId: null,
    assetMapIconId: null,
    isGateway: false,
    hideGatewayData: false,
    gatewayAssetId: null,
    productType: "",
    productModel: "",
    productIdentifier: "",
    productManufacturer: "",
    controllerModel: null,
    controllerManufacturer: null,
    modbusSensorId: -1,
    modbusSensorSlaveId: -1,
    deviceType: null,
    deviceIdentifier: null,
    gpsLatitude: String(GOOGLE_MAPS_DEFAULT_LAT),
    gpsLongitude: String(GOOGLE_MAPS_DEFAULT_LNG),
    isRented: false,
    analogInputs: [],
    digitalInputs: [],
    digitalOutputs: [],
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showConfirmExit, setShowConfirmExit] = useState<boolean>(false);
  const [showConfirmTemplateConfig, setShowConfirmTemplateConfig] = useState<boolean>(false);
  const [showConfirmSensorConfig, setShowConfirmSensorConfig] = useState<boolean>(false);
  const [activePageNumber, setActivePageNumber] = useState<number>(1);
  const [isRented, setIsRented] = useState<boolean>(false);
  const [isMigrating, setIsMigrating] = useState<boolean>(false);
  const [lastConfigSuccessful, setLastConfigSuccessful] = useState<boolean>(true);
  const [lastConfigAttemptUtc, setLastConfigAttemptUtc] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [nickname, setNickname] = useState<string>("");
  const [assetTemplateId, setAssetTemplateId] = useState<number | null>(null);
  const [assetMapIconId, setAssetMapIconId] = useState<number | null>(null);
  const [dataCollectionId, setDataCollectionId] = useState<number>(-1);
  const [deviceType, setDeviceType] = useState<string | null>(null);
  const [gatewayDeviceType, setGatewayDeviceType] = useState<string | null>(null);
  const [deviceIdentifier, setDeviceIdentifier] = useState<string | null>(null);
  const [isGateway, setIsGateway] = useState<boolean>(false);
  const [hideGatewayData, setHideGatewayData] = useState<boolean>(false);
  const [gatewayAssetId, setGatewayAssetId] = useState<number | null>(null);
  const [gatewayAssetName, setGatewayAssetName] = useState<string>("");
  const [analogInputsInUse, setAnalogInputsInUse] = useState<SensorInfoMap | null>(null);
  const [digitalInputsInUse, setDigitalInputsInUse] = useState<SensorInfoMap | null>(null);
  const [digitalOutputsInUse, setDigitalOutputsInUse] = useState<SensorInfoMap | null>(null);
  const [modbusInfoInUse, setModbusInfoInUse] = useState<ModbusInfo | null>(null);
  const [controllerInUse, setControllerInUse] = useState<ControllerInfo | null>(null);
  const [productType, setProductType] = useState<string>("");
  const [productManufacturer, setProductManufacturer] = useState<string>("");
  const [otherProductManufacturer, setOtherProductManufacturer] = useState<string>("");
  const [productModel, setProductModel] = useState<string>("");
  const [productIdentifier, setProductIdentifier] = useState<string>("");
  const [modbusMedium, setModbusMedium] = useState<ModbusMedium>("Not used");
  const [controllerIndex, setControllerIndex] = useState<number>(-1);
  const [modbusSensorId, setModbusSensorId] = useState<number>(-1);
  const [modbusSensorPosition, setModbusSensorPosition] = useState<number>(-1);
  const [analogSensorMap, setAnalogSensorMap] = useState<AnalogSensorMap>({});
  const [analogInputCards, setAnalogInputCards] = useState<AnalogInputCard[]>([]);
  const [digitalSensorMap, setDigitalSensorMap] = useState<DigitalSensorMap>({});
  const [digitalInputCards, setDigitalInputCards] = useState<DigitalInputCard[]>([]);
  const [digitalOutputCards, setDigitalOutputCards] = useState<DigitalOutputCard[]>([]);
  const [serviceAttribute, setServiceAttribute] = useState<string | null>(null);
  const [serviceTime, setServiceTime] = useState<string>("");
  const [updateServiceTimeManually, setUpdateServiceTimeManually] = useState<boolean>(false);
  const [updateLocationManually, setUpdateLocationManually] = useState<boolean>(false);
  const [latitude, setLatitude] = useState<string>(String(GOOGLE_MAPS_DEFAULT_LAT));
  const [longitude, setLongitude] = useState<string>(String(GOOGLE_MAPS_DEFAULT_LNG));
  const [previousAsset, setPreviousAsset] = useState<AssetConfiguration>(initialAsset);
  const [previousDataCollectionId, setPreviousDataCollectionId] = useState<number>(-1);
  const [previousServiceAttribute, setPreviousServiceAttribute] = useState<string | null>(null);
  const [previousServiceTime, setPreviousServiceTime] = useState<string>("");
  const [previousLatitude, setPreviousLatitude] = useState<string>(String(GOOGLE_MAPS_DEFAULT_LAT));
  const [previousLongitude, setPreviousLongitude] = useState<string>(String(GOOGLE_MAPS_DEFAULT_LNG));
  const [breadcrumbs, setBreadcrumbs] = useState<PageTitle[]>([]);
  const [maxAnalogInputCount, maxDigitalInputCount, maxDigitalOutputCount] = useMemo(
    () => getMaxSensorCounts(deviceType, gatewayDeviceType, props.deviceTypes),
    [deviceType, gatewayDeviceType, JSON.stringify(props.deviceTypes)]
  );
  const currentUser = useReduxSelector((state) => state.currentUser);
  const activePageTitle = useMemo(
    () => getPageTitle(activePageNumber, breadcrumbs),
    [activePageNumber, JSON.stringify(breadcrumbs)]
  );
  const isModbusInUse = useMemo(
    () => getIsModbusInUse(modbusMedium, modbusInfoInUse, controllerInUse),
    [modbusMedium, JSON.stringify(modbusInfoInUse), JSON.stringify(controllerInUse)]
  );
  const isScadaRtuDeviceType = useMemo(
    () => getIsScadaRtuDeviceType(deviceType, gatewayDeviceType),
    [deviceType, gatewayDeviceType]
  );
  const hasDigitalOutputs = useMemo(
    () => getHasDigitalOutputs(digitalOutputCards, digitalOutputsInUse),
    [JSON.stringify(digitalOutputCards), JSON.stringify(digitalOutputsInUse)]
  );

  // Retrieves info on which connections (Modbus, analog, digital, etc.) are in use within the current network.
  useApi(
    () => {
      if (props.showModal && isGateway && previousAsset.isGateway) {
        // This is a gateway asset, that was a gateway prior to any unsaved modifications and needs network information.
        setLoading(true);
        return true;
      } else if (props.showModal && !isGateway && gatewayAssetId !== null) {
        // This is a non-gateway asset that needs to retrieve network information.
        setLoading(true);
        return true;
      } else {
        return false;
      }
    },
    {
      method: "POST",
      url: `${API}/company/${currentUser.companyId}/asset-network-configuration`,
      body: {
        currentAssetId: props.assetId > 0 ? props.assetId : null,
        gatewayAssetId: isGateway ? props.assetId : gatewayAssetId,
      },
    },
    async (response: Response, responseBody: GatewayConfigurationResponseBody) => {
      if (response.ok && responseBody) {
        setAnalogInputsInUse(responseBody.analogInputs);
        setDigitalInputsInUse(responseBody.digitalInputs);
        setDigitalOutputsInUse(responseBody.digitalOutputs);
        setModbusInfoInUse(responseBody.modbusSensors);
        setControllerInUse(responseBody.controller);
        setGatewayDeviceType(responseBody.gatewayDeviceType);
        setErrorMessage("");
      } else {
        setErrorMessage(await getApiError(response, "Unable to get network configuration data."));
      }
      setLoading(false);
    },
    [props.assetId, props.showModal, gatewayAssetId, isGateway, JSON.stringify(previousAsset)]
  );

  // Retrieves all asset information relating to the selected asset (e.g. for editing existing assets).
  useApi(
    () => {
      if (props.assetId > 0 && props.showModal) {
        setLoading(true);
        return true;
      } else {
        return false;
      }
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/${props.assetId}/configuration`,
    },
    async (response: Response, responseBody: GetAssetConfigurationResponseBody) => {
      if (response.ok && responseBody) {
        setName(responseBody.name);
        setNickname(responseBody.nickname);
        setAssetTemplateId(responseBody.assetTemplateId);
        setAssetMapIconId(responseBody.assetMapIconId);
        setDeviceType(responseBody.deviceType);

        const [parsedLatitude, parsedLongitude] = convertLocationToLatitudeAndLongitude(responseBody.currentLocation);
        setLatitude(parsedLatitude);
        setLongitude(parsedLongitude);
        setPreviousLatitude(parsedLatitude);
        setPreviousLongitude(parsedLongitude);

        // Populate the data collection ID based on if the device type or gateway asset ID is populated or not.
        if (
          responseBody.deviceType !== null &&
          responseBody.deviceType !== undefined &&
          responseBody.deviceType.length > 0
        ) {
          setPreviousDataCollectionId(DATA_COLLECTION_MONITORING_DEVICE);
          setDataCollectionId(DATA_COLLECTION_MONITORING_DEVICE);
        } else if (responseBody.gatewayAssetId !== null && responseBody.gatewayAssetId !== undefined) {
          setPreviousDataCollectionId(DATA_COLLECTION_OTHER_ASSET);
          setDataCollectionId(DATA_COLLECTION_OTHER_ASSET);
        } else {
          setPreviousDataCollectionId(DATA_COLLECTION_NONE);
          setDataCollectionId(DATA_COLLECTION_NONE);
        }

        setDeviceIdentifier(responseBody.deviceIdentifier);

        setIsGateway(responseBody.isGateway);
        setHideGatewayData(responseBody.hideGatewayData);
        setGatewayAssetId(responseBody.gatewayAssetId);

        // Set the gateway asset name based on the provided gateway asset ID (if one exists).
        if (responseBody.gatewayAssetId !== null && responseBody.gatewayAssetId !== undefined) {
          const newGatewayAsset = props.gatewayAssets.find((asset) => asset.assetId === responseBody.gatewayAssetId);
          if (newGatewayAsset !== null && newGatewayAsset !== undefined) {
            setGatewayAssetName(newGatewayAsset.name);
          } else {
            setGatewayAssetName("");
          }
        } else {
          setGatewayAssetName("");
        }

        setProductType(responseBody.productType);
        setProductModel(responseBody.productModel);
        setProductIdentifier(responseBody.productIdentifier);

        // Find the controller index to use.
        const newController = props.controllers.find(
          (controller) =>
            controller.controllerModel === responseBody.controllerModel &&
            controller.manufacturerCompanyCode === responseBody.controllerManufacturer
        );
        if (newController !== undefined) {
          setControllerIndex(newController.controllerId);
        } else {
          setControllerIndex(-1);
        }

        let currentModbusSensorId = -1;
        if (responseBody.modbusSensorId !== null) {
          currentModbusSensorId = responseBody.modbusSensorId;
        }
        setModbusSensorId(currentModbusSensorId);

        let currentModbusSensorPosition = -1;
        if (responseBody.modbusSensorSlaveId !== null) {
          currentModbusSensorPosition = responseBody.modbusSensorSlaveId;
        }
        setModbusSensorPosition(currentModbusSensorPosition);

        setPreviousAsset({
          ...responseBody,
          modbusSensorId: currentModbusSensorId,
          modbusSensorSlaveId: currentModbusSensorPosition,
          gpsLatitude: latitude,
          gpsLongitude: longitude,
        });

        // Update modbusMedium based on if a controller or a modbus sensor ID is provided.
        if (newController !== undefined) {
          setModbusMedium("Controller");
        } else if (responseBody.modbusSensorId !== null && responseBody.modbusSensorId >= 0) {
          setModbusMedium("Sensor");
        } else {
          setModbusMedium("Not used");
        }

        // Find the product manufacturer.
        if (
          responseBody.productManufacturer === "" ||
          props.productManufacturers.includes(responseBody.productManufacturer)
        ) {
          setProductManufacturer(responseBody.productManufacturer);
          setOtherProductManufacturer("");
        } else {
          setProductManufacturer("OTHER");
          setOtherProductManufacturer(responseBody.productManufacturer);
        }

        // Setup the analog sensor map.
        const newAnalogSensorMap: AnalogSensorMap = {};
        responseBody.analogInputs.forEach((analogSensor) => {
          newAnalogSensorMap[analogSensor.attributeCode] = analogSensor;
        });
        setAnalogSensorMap(newAnalogSensorMap);

        // Setup the analog sensor cards by converting each entry in the analog sensor map into an analog sensor card.
        // Each analog sensor card is used to display one sensor on the AnalogInputPage.
        const newAnalogInputCards: AnalogInputCard[] = [];
        Object.entries(newAnalogSensorMap).forEach(([attributeCode, analogSensor], i) => {
          // Find the sensor position based on the current analog sensor's attribute code.
          let sensorNumber = i + 1;
          const sensorNumberMatch = analogSensor.attributeCode.match(/AI(\d+)Engineering/i);
          if (sensorNumberMatch) {
            sensorNumber = parseInt(sensorNumberMatch[1], 10);
          }
          // Construct a new and unique analog sensor card using data from the current analog sensor map entry.
          const newAnalogInputCard: AnalogInputCard = {
            formId: i + 1,
            sensorNumber: sensorNumber,
            analogSensorId: analogSensor.analogSensorId,
            attributeId: analogSensor.mappedAttributeId,
            attributeCode: attributeCode,
          };
          newAnalogInputCards.push(newAnalogInputCard);
        });
        setAnalogInputCards(newAnalogInputCards);

        // Setup the digital sensor map.
        const previousDigitalSensors: DigitalInputSelection[] = [];
        const newDigitalSensorMap: DigitalSensorMap = {};
        responseBody.digitalInputs.forEach((digitalSensor) => {
          newDigitalSensorMap[digitalSensor.attributeStateCode] = digitalSensor;
          previousDigitalSensors.push(digitalSensor);
        });
        setDigitalSensorMap(newDigitalSensorMap);

        // Setup the digital input cards by converting each entry in the digital sensor map into a digital input card.
        // Each digital input card is used to display one sensor on the DigitalInputPage.
        const newDigitalInputCards: DigitalInputCard[] = [];
        Object.entries(newDigitalSensorMap).forEach(([attributeStateCode, digitalSensor], i) => {
          // Find the sensor position based on the current digital sensor's attribute state code.
          let sensorNumber = i + 1;
          const sensorNumberMatch = digitalSensor.attributeStateCode.match(/DI(\d+)State/i);
          if (sensorNumberMatch) {
            sensorNumber = parseInt(sensorNumberMatch[1], 10);
          }
          // Construct a new and unique digital sensor card using data from the current digital sensor map entry.
          const newDigitalInputCard: DigitalInputCard = {
            formId: i + 1,
            sensorNumber: sensorNumber,
            digitalSensorId: digitalSensor.digitalSensorId,
            attributeStateCode: attributeStateCode,
            reportingDescription: digitalSensor.reportingDescription,
          };
          newDigitalInputCards.push(newDigitalInputCard);
        });
        setDigitalInputCards(newDigitalInputCards);

        // Setup the digital output cards for use on the DigitalOutputPage.
        const newDigitalOutputCards: DigitalOutputCard[] = [];
        responseBody.digitalOutputs.forEach((digitalOutput, i) => {
          const newDigitalOutputCard: DigitalOutputCard = {
            formId: i + 1,
            position: digitalOutput.position,
            operationLabel: digitalOutput.operationLabel,
            trueLabel: digitalOutput.trueLabel,
            falseLabel: digitalOutput.falseLabel,
          };
          newDigitalOutputCards.push(newDigitalOutputCard);
        });
        setDigitalOutputCards(newDigitalOutputCards);

        if (responseBody.lastConfigAttemptUtc !== undefined && responseBody.lastConfigAttemptUtc !== null) {
          setLastConfigAttemptUtc(responseBody.lastConfigAttemptUtc);
        }
        if (responseBody.lastConfigSuccessful !== undefined && responseBody.lastConfigSuccessful !== null) {
          setLastConfigSuccessful(responseBody.lastConfigSuccessful);
        }
        if (responseBody.serviceHoursAttributeCode !== undefined && responseBody.serviceHoursAttributeCode !== null) {
          setPreviousServiceAttribute(responseBody.serviceHoursAttributeCode);
          setServiceAttribute(responseBody.serviceHoursAttributeCode);
        }
        if (responseBody.runtimeSinceLastServiced !== undefined && responseBody.runtimeSinceLastServiced !== null) {
          setPreviousServiceTime(String(responseBody.runtimeSinceLastServiced));
          setServiceTime(String(responseBody.runtimeSinceLastServiced));
        } else {
          setPreviousServiceTime("");
          setServiceTime("");
        }

        setIsRented(responseBody.isRented);
        setIsMigrating(responseBody.isMigrating);

        setErrorMessage("");
      } else {
        setErrorMessage(await getApiError(response, "Unable to get asset settings."));
      }
      setLoading(false);
    },
    [
      props.assetId,
      props.showModal,
      JSON.stringify(currentUser),
      JSON.stringify(props.gatewayAssets),
      JSON.stringify(props.controllers),
      JSON.stringify(props.productManufacturers),
    ]
  );

  // If an initial asset is defined and we are creating a new asset, set all asset details to the initial asset.
  useEffect(() => {
    if (props.isCreatingNewAsset && props.initialAsset !== undefined) {
      setPreviousAsset(props.initialAsset);
      setName(props.initialAsset.name);
      setNickname(props.initialAsset.nickname);
      setProductType(props.initialAsset.productType);
      setAssetTemplateId(props.initialAsset.assetTemplateId);
      setProductType(props.initialAsset.productType);
      setProductManufacturer(props.initialAsset.productManufacturer);
      setProductModel(props.initialAsset.productModel);
      setProductIdentifier(props.initialAsset.productIdentifier);
      setIsRented(props.initialAsset.isRented);
      setIsMigrating(false);
      setDeviceType(props.initialAsset.deviceType);
      setDeviceIdentifier(props.initialAsset.deviceIdentifier);
      setAnalogSensorMap({});
      setDigitalSensorMap({});

      const initialController = props.controllers.find(
        (controller) =>
          props.initialAsset !== undefined &&
          controller.controllerModel === props.initialAsset.controllerModel &&
          controller.manufacturerCompanyCode === props.initialAsset.controllerManufacturer
      );
      if (initialController !== undefined) {
        setControllerIndex(initialController.controllerId);
      } else {
        setControllerIndex(-1);
      }

      if (
        props.initialAsset.deviceType !== null &&
        props.initialAsset.deviceType !== undefined &&
        props.initialAsset.deviceIdentifier !== null &&
        props.initialAsset.deviceIdentifier !== undefined
      ) {
        setPreviousDataCollectionId(DATA_COLLECTION_MONITORING_DEVICE);
        setDataCollectionId(DATA_COLLECTION_MONITORING_DEVICE);
      } else if (props.initialAsset.gatewayAssetId !== null && props.initialAsset.gatewayAssetId !== undefined) {
        setPreviousDataCollectionId(DATA_COLLECTION_OTHER_ASSET);
        setDataCollectionId(DATA_COLLECTION_OTHER_ASSET);
      } else {
        setPreviousDataCollectionId(DATA_COLLECTION_NONE);
        setDataCollectionId(DATA_COLLECTION_NONE);
      }

      setIsGateway(props.initialAsset.isGateway);
      setHideGatewayData(props.initialAsset.hideGatewayData);
      setGatewayAssetId(props.initialAsset.gatewayAssetId);
      setGatewayAssetName("");
      setModbusSensorId(props.initialAsset.modbusSensorId || -1);
      setModbusSensorPosition(props.initialAsset.modbusSensorSlaveId || -1);

      if (initialController !== undefined) {
        setModbusMedium("Controller");
      } else if (props.initialAsset.modbusSensorId !== null && props.initialAsset.modbusSensorId >= 0) {
        setModbusMedium("Sensor");
      } else {
        setModbusMedium("Not used");
      }
    }
  }, [
    JSON.stringify(props.initialAsset),
    JSON.stringify(props.controllers),
    props.isCreatingNewAsset,
    props.showModal,
  ]);

  // Clear error messages and loading state whenever the active page number changes.
  useEffect(() => {
    setErrorMessage("");
    setLoading(false);
  }, [activePageNumber]);

  // Updates the list of breadcrumb pages whenever the data collection type or device type are updated.
  useEffect(() => {
    const newBreadcrumbs = getBreadcrumbs(
      dataCollectionId,
      maxAnalogInputCount,
      maxDigitalInputCount,
      maxDigitalOutputCount,
      gatewayAssetId
    );
    setBreadcrumbs(newBreadcrumbs);
  }, [dataCollectionId, maxAnalogInputCount, maxDigitalInputCount, maxDigitalOutputCount, gatewayAssetId]);

  // Update the modbus controller and sensor values if the modbus medium radio is changed
  // to make sure unused values are cleared.
  useEffect(() => {
    if (modbusMedium === "Controller") {
      setModbusSensorId(-1);
      setModbusSensorPosition(-1);
    } else if (modbusMedium === "Sensor") {
      setControllerIndex(-1);
    } else {
      setControllerIndex(-1);
      setModbusSensorId(-1);
      setModbusSensorPosition(-1);
    }
  }, [modbusMedium]);

  // Reset latitude and longitude to their previous values if the checkbox to manually update location gets unchecked.
  // Also make sure that the previous latitude and longitude are stored with other previous asset data.
  useEffect(() => {
    if (!updateLocationManually) {
      setLatitude(previousLatitude);
      setLongitude(previousLongitude);
    }
    setPreviousAsset((prev) => {
      return { ...prev, gpsLatitude: previousLatitude, gpsLongitude: previousLongitude };
    });
  }, [updateLocationManually, previousLatitude, previousLongitude]);

  // Given a page and the breadcrumbs, gets the current page title.
  function getPageTitle(pageNumber: number, breadcrumbs: PageTitle[]): string {
    if (pageNumber <= breadcrumbs.length) {
      return breadcrumbs[pageNumber - 1];
    } else {
      return "";
    }
  }

  // Gets the currently selected device type's max sensor counts.
  function getMaxSensorCounts(
    deviceType: string | null,
    gatewayDeviceType: string | null,
    deviceTypes: DeviceType[]
  ): [number, number, number] {
    let currentDeviceType: string | null = null;
    let newMaxAnalogInputCount = 0;
    let newMaxDigitalInputCount = 0;
    let newMaxDigitalOutputCount = 0;
    if (gatewayDeviceType !== null) {
      currentDeviceType = gatewayDeviceType;
    } else {
      currentDeviceType = deviceType;
    }
    const currentDeviceTypeWithSensors = deviceTypes.find((type) => type.model === currentDeviceType);

    // Calculate the new max analog input count.
    if (currentDeviceTypeWithSensors !== null && currentDeviceTypeWithSensors !== undefined) {
      newMaxAnalogInputCount = currentDeviceTypeWithSensors.maxAnalogInputCount;
      newMaxDigitalInputCount = currentDeviceTypeWithSensors.maxDigitalInputCount;
      newMaxDigitalOutputCount = currentDeviceTypeWithSensors.maxDigitalOutputCount;
    }

    return [newMaxAnalogInputCount, newMaxDigitalInputCount, newMaxDigitalOutputCount];
  }

  // Gets the correct list of breadcrumb pages based on the data collection type selected.
  function getBreadcrumbs(
    currentDataCollectionId: number,
    currentMaxAnalogInputCount: number,
    currentMaxDigitalInputCount: number,
    currentMaxDigitalOutputCount: number,
    currentGatewayAssetId: number | null
  ): PageTitle[] {
    if (currentDataCollectionId === DATA_COLLECTION_NONE) {
      return ["Asset Details", "Product", "Data"];
    } else if (
      currentDataCollectionId === DATA_COLLECTION_MONITORING_DEVICE ||
      currentDataCollectionId === DATA_COLLECTION_OTHER_ASSET
    ) {
      const breadcrumbs: PageTitle[] = ["Asset Details", "Product"];
      if (currentMaxAnalogInputCount > 0 || currentMaxDigitalInputCount > 0 || currentGatewayAssetId !== null) {
        breadcrumbs.push("Modbus");
      }
      if (currentMaxAnalogInputCount > 0) {
        breadcrumbs.push("Analog Input");
      }
      if (currentMaxDigitalInputCount > 0) {
        breadcrumbs.push("Digital Input");
      }
      if (currentMaxDigitalOutputCount > 0) {
        breadcrumbs.push("Digital Output");
      }
      breadcrumbs.push("Data");
      return breadcrumbs;
    } else {
      return ["Asset Details"];
    }
  }

  // Returns whether the current asset or another asset in the network (if the current asset is in one) is using Modbus.
  function getIsModbusInUse(
    modbusMedium: string,
    modbusInfoInUse: ModbusInfo | null,
    controllerInUse: ControllerInfo | null
  ): boolean {
    if (modbusMedium !== "Not used" || modbusInfoInUse !== null || controllerInUse !== null) {
      return true;
    } else {
      return false;
    }
  }

  // Returns whether the current asset or another asset in the network
  // (if the current asset is in one) has a SCADA RTU device type.
  function getIsScadaRtuDeviceType(deviceType: string | null, gatewayDeviceType: string | null): boolean {
    if ([deviceType, gatewayDeviceType].includes(SCADA_RTU_DEVICE_TYPE)) {
      return true;
    } else {
      return false;
    }
  }

  // Returns whether the current asset or another asset in the network
  // (if the current asset is in one) has digital output sensors configured.
  function getHasDigitalOutputs(
    digitalOutputCards: DigitalOutputCard[],
    digitalOutputsInUse: SensorInfoMap | null
  ): boolean {
    if (
      digitalOutputCards.length > 0 ||
      (digitalOutputsInUse !== null && Object.entries(digitalOutputsInUse).length > 0)
    ) {
      return true;
    } else {
      return false;
    }
  }

  // Take a location as a string or null value, and converts it into two separate latitude and longitude values.
  function convertLocationToLatitudeAndLongitude(locationValue: string | null): [string, string] {
    let latitude = String(GOOGLE_MAPS_DEFAULT_LAT);
    let longitude = String(GOOGLE_MAPS_DEFAULT_LNG);
    if (locationValue !== null) {
      const gpsCoords = locationValue.split(",");
      if (gpsCoords.length === 2) {
        latitude = gpsCoords[0];
        longitude = gpsCoords[1];
      }
    }
    return [latitude, longitude];
  }

  // Determines if the form is editable based on the user's permissions.
  function formIsEditable(): boolean {
    return (
      (props.isCreatingNewAsset && userHasPermission([[CREATE_ASSETS_PERMISSION]])) ||
      (!props.isCreatingNewAsset && userHasPermission([[UPDATE_ASSETS_PERMISSION]]))
    );
  }

  // When the user changes the currently selected data collection method, clear all related fields so that
  // there isn't a conflict with selection options and also update the selected data collection ID.
  // Ex: Don't allow a user to set a controller and then switch to getting data from another asset that is already
  // using a Modbus sensor, which leads to a conflict where two different Modbus methods are used.
  function handleDataCollectionIdChange(dataCollectionId: number): void {
    setDeviceType(null);
    setDeviceIdentifier(null);
    setIsGateway(false);
    setGatewayAssetId(null);
    setGatewayAssetName("");
    setHideGatewayData(false);
    setModbusMedium("Not used");
    setControllerIndex(-1);
    setModbusSensorId(-1);
    setModbusSensorPosition(-1);
    setAnalogSensorMap({});
    setAnalogInputCards([]);
    setDigitalSensorMap({});
    setDigitalInputCards([]);
    setDigitalOutputCards([]);
    setDataCollectionId(dataCollectionId);
  }

  // When the user updates a monitoring device to change its gateway status, make sure related values are also updated.
  //
  // Disabling gateway: When disabling a gateway, make sure all locked fields become available to edit again.
  // Ex: If Modbus is in use due to a network asset controller, disabling should enable Modbus selections.
  //
  // Enabling gateway: When enabling a gateway, we should clear all existing Modbus and sensor fields since
  // they might conflict with reserved fields for already connected network assets.
  function handleIsGateway(isGateway: boolean): void {
    if (isGateway) {
      setModbusMedium("Not used");
      setControllerIndex(-1);
      setModbusSensorId(-1);
      setModbusSensorPosition(-1);
      setAnalogSensorMap({});
      setAnalogInputCards([]);
      setDigitalSensorMap({});
      setDigitalInputCards([]);
      setDigitalOutputCards([]);
    } else {
      setAnalogInputsInUse(null);
      setDigitalInputsInUse(null);
      setDigitalOutputsInUse(null);
      setModbusInfoInUse(null);
      setControllerInUse(null);
      setGatewayDeviceType(null);
    }
    setIsGateway(isGateway);
  }

  // Searches for a matching gateway asset.
  function searchGatewayAssets(filter: string): void {
    setGatewayAssetName(filter);
    setErrorMessage("");
    const foundGatewayAsset = props.gatewayAssets.find((asset) => asset.name === filter);

    if (foundGatewayAsset === undefined) {
      setGatewayAssetId(null);
    } else {
      setGatewayAssetId(foundGatewayAsset.assetId);
    }
  }

  // Changes an analog input sensor that is connected to the monitoring device.
  function updateAnalogInput(
    formId: number,
    sensorNumber: number,
    analogSensorId: number | null,
    attributeId: number | null,
    attributeCode: string
  ): void {
    const tempAnalogSensors = deepCopy(props.analogSensors);
    const tempAnalogSensorMap = deepCopy(analogSensorMap);

    // Only parse the sensor information if valid sensor positions and IDs (i.e. types) are selected.
    if (analogSensorId !== null && sensorNumber <= ANALOG_INPUTS.length) {
      const analogSensor = tempAnalogSensors.find((analogSensor) => analogSensor.analogSensorId === analogSensorId);
      const analogInputs = ANALOG_INPUTS[sensorNumber - 1];
      if (
        (attributeId === null || attributeId <= 0) &&
        analogSensor !== undefined &&
        analogSensor.applications.length > 0
      ) {
        // A valid sensor ID was given, so update the sensor based on the attribute code.
        const sensorSelection = {
          analogSensorId: analogSensorId,
          attributeCode: attributeCode,
          attributeConnectedCode: analogInputs.connectedAttribute,
          attributeModeCode: analogInputs.modeAttribute,
          attributeMinCode: analogInputs.minAttribute,
          attributeSpanCode: analogInputs.spanAttribute,
          mappedAttributeId: -1,
          mappedAttributeConnectedId: -1,
        };
        tempAnalogSensorMap[analogInputs.attribute] = sensorSelection;

        setAnalogSensorMap(tempAnalogSensorMap);
      } else if (attributeId !== null && attributeId > 0 && analogSensor !== undefined) {
        // A valid sensor ID and attribute ID was given, so update the attribute based on the attribute code.
        const application = analogSensor.applications.find((application) => application.attributeId === attributeId);
        if (application !== undefined) {
          const sensorSelection = {
            analogSensorId: analogSensorId,
            attributeCode: attributeCode,
            attributeConnectedCode: analogInputs.connectedAttribute,
            attributeModeCode: analogInputs.modeAttribute,
            attributeMinCode: analogInputs.minAttribute,
            attributeSpanCode: analogInputs.spanAttribute,
            mappedAttributeId: application.attributeId,
            mappedAttributeConnectedId: application.attributeConnectedId,
          };
          tempAnalogSensorMap[analogInputs.attribute] = sensorSelection;

          // Delete the old sensor in the analog sensor map that is being replaced by the current sensor.
          // Note: this mainly happens when the sensor position is changed.
          const replacedAnalogSensor = analogInputCards.find(
            (card) =>
              card.analogSensorId === analogSensorId && card.sensorNumber !== sensorNumber && card.formId === formId
          );
          if (replacedAnalogSensor !== undefined) {
            const oldSensorNumber = replacedAnalogSensor.sensorNumber;
            const oldAnalogInputs = ANALOG_INPUTS[oldSensorNumber - 1];
            delete tempAnalogSensorMap[oldAnalogInputs.attribute];
          }

          setAnalogSensorMap(tempAnalogSensorMap);
        }
      }
    } else {
      // If nothing is selected, then set the selected application(s) to none.
      delete tempAnalogSensorMap[attributeCode];
      setAnalogSensorMap(tempAnalogSensorMap);
    }
  }

  // Adds a new analog sensor card to the Analog Page.
  function handleCreateAnalogInput(): void {
    const analogInputCardsDeepCopy = deepCopy(analogInputCards);
    let formId = 1;
    const sensorNumber = -1;
    analogInputCardsDeepCopy.forEach((analogSensor) => {
      if (analogSensor.formId >= formId) {
        formId = analogSensor.formId + 1;
      }
    });

    const analogSensor: AnalogInputCard = {
      formId: formId,
      sensorNumber: sensorNumber,
      analogSensorId: null,
      attributeId: null,
      attributeCode: "",
    };
    analogInputCardsDeepCopy.push(analogSensor);
    setAnalogInputCards(analogInputCardsDeepCopy);
  }

  // Updates an analog sensor on the Analog Page.
  function handleUpdateAnalogInput(
    formId: number,
    sensorNumber: number,
    analogSensorId: number | null,
    attributeId: number | null,
    attributeCode: string
  ): void {
    const analogInputCardsDeepCopy = deepCopy(analogInputCards);
    const analogSensor = analogInputCardsDeepCopy.find((analogSensor) => analogSensor.formId === formId);
    let newAttributeId: number | null = null;
    if (analogSensor !== undefined) {
      // Clear the reporting description if the sensor type is changed.
      if (analogSensor.analogSensorId === analogSensorId) {
        newAttributeId = attributeId;
      }
      analogSensor.sensorNumber = sensorNumber;
      analogSensor.analogSensorId = analogSensorId;
      analogSensor.attributeId = newAttributeId;
      analogSensor.attributeCode = attributeCode;
    }
    setAnalogInputCards(analogInputCardsDeepCopy);

    // Reflect the analog sensor card changes in the analog sensor map, adding the sensor if it is new.
    // NOTE: Analog sensors are not being added whenever a new card is created because an analogSensorId is needed.
    updateAnalogInput(formId, sensorNumber, analogSensorId, newAttributeId, attributeCode);
  }

  // Deletes an analog input sensor on the Analog Input Page.
  function handleDeleteAnalogInput(formId: number, sensorNumber: number, attributeCode: string): void {
    const analogInputCardsDeepCopy = deepCopy(analogInputCards);
    const selectedAnalogSensorIndex = analogInputCardsDeepCopy.findIndex(
      (analogSensor) => analogSensor.formId === formId
    );
    if (selectedAnalogSensorIndex >= 0) {
      analogInputCardsDeepCopy.splice(selectedAnalogSensorIndex, 1);
      setAnalogInputCards(analogInputCardsDeepCopy);
    }

    // Reflect the deletion of the sensor card changes in the analog sensor map.
    updateAnalogInput(formId, sensorNumber, null, null, attributeCode);
  }

  // Changes a digital sensor that is connected to the monitoring device.
  function updateDigitalInput(
    formId: number,
    sensorNumber: number,
    digitalSensorId: number | null,
    attributeStateCode: string,
    reportingDescription: string
  ): void {
    const tempDigitalSensors = deepCopy(props.digitalSensors);
    const tempDigitalSensorMap = deepCopy(digitalSensorMap);

    if (tempDigitalSensorMap !== null && sensorNumber <= DIGITAL_INPUTS.length) {
      const digitalInputs = DIGITAL_INPUTS[sensorNumber - 1];
      if (digitalSensorId !== null) {
        const digitalSensor = tempDigitalSensors.find(
          (digitalSensor) => digitalSensor.digitalSensorId === digitalSensorId
        );
        if (digitalSensor !== undefined) {
          if (reportingDescription.length === 0 && digitalSensor.applications.length > 0) {
            // A valid sensor ID was given, so update the sensor based on the attribute codes.
            const sensorSelection = {
              digitalSensorId: digitalSensorId,
              attributeStateCode: attributeStateCode,
              attributeFlowCode: digitalInputs.flowAttribute,
              attributeAccumulationCode: digitalInputs.accumulationAttribute,
              attributeModeCode: digitalInputs.modeAttribute,
              attributeKFactorCode: digitalInputs.kFactorAttribute,
              attributeOffsetCode: digitalInputs.offsetAttribute,
              attributeDebounceCode: digitalInputs.debounceAttribute,
              attributeUnitsPerPulseCode: digitalInputs.unitsPerPulseAttribute,
              mappedAttributeStateId: -1,
              mappedAttributeFlowId: -1,
              mappedAttributeAccumulationId: -1,
              reportingDescription: "",
            };
            tempDigitalSensorMap[attributeStateCode] = sensorSelection;
            setDigitalSensorMap(tempDigitalSensorMap);
          } else if (reportingDescription.length !== 0) {
            // A valid sensor ID and reporting description were given,
            // so update the application based on the reporting description.
            const application = digitalSensor.applications.find(
              (application) => application.reportingDescription === reportingDescription
            );
            if (application !== undefined) {
              const sensorSelection = {
                digitalSensorId: digitalSensorId,
                attributeStateCode: attributeStateCode,
                attributeFlowCode: digitalInputs.flowAttribute,
                attributeAccumulationCode: digitalInputs.accumulationAttribute,
                attributeModeCode: digitalInputs.modeAttribute,
                attributeKFactorCode: digitalInputs.kFactorAttribute,
                attributeOffsetCode: digitalInputs.offsetAttribute,
                attributeDebounceCode: digitalInputs.debounceAttribute,
                attributeUnitsPerPulseCode: digitalInputs.unitsPerPulseAttribute,
                mappedAttributeStateId: application.attributeStateId,
                mappedAttributeFlowId: application.attributeFlowId,
                mappedAttributeAccumulationId: application.attributeAccumulationId,
                reportingDescription: application.reportingDescription,
              };
              tempDigitalSensorMap[attributeStateCode] = sensorSelection;

              // Delete the old sensor in the digital sensor map that is being replaced by the current sensor.
              // Note: this mainly happens when the sensor position is changed.
              const replacedDigitalSensor = digitalInputCards.find(
                (card) =>
                  card.digitalSensorId === digitalSensorId &&
                  card.sensorNumber !== sensorNumber &&
                  card.formId === formId
              );
              if (replacedDigitalSensor !== undefined) {
                const oldSensorNumber = replacedDigitalSensor.sensorNumber;
                const oldDigitalInputs = DIGITAL_INPUTS[oldSensorNumber - 1];
                delete tempDigitalSensorMap[oldDigitalInputs.stateAttribute];
              }

              setDigitalSensorMap(tempDigitalSensorMap);
            }
          }
        }
      } else {
        // The SCADA RTU only supports certain sensors on different sensorNumbers so when we switch sensorNumber we
        // clear the digitalSensor card by sending a digitalSensorId of null when updating the cards. This will not
        // update the digitalSensorMap so we need to check for the replaced digital sensor and delete it from the map.
        const replacedDigitalSensor = digitalInputCards.find(
          (card) => card.sensorNumber !== sensorNumber && card.formId === formId
        );
        if (replacedDigitalSensor !== undefined) {
          const oldSensorNumber = replacedDigitalSensor.sensorNumber;
          const oldDigitalInputs = DIGITAL_INPUTS[oldSensorNumber - 1];
          delete tempDigitalSensorMap[oldDigitalInputs.stateAttribute];
        }
        // If nothing is selected, then set the selected application(s) to none.
        delete tempDigitalSensorMap[attributeStateCode];
        setDigitalSensorMap(tempDigitalSensorMap);
      }
    }
  }

  // Adds a new digital sensor card to the digital input page.
  function handleCreateDigitalInput(): void {
    const digitalInputCardsDeepCopy = deepCopy(digitalInputCards);
    let formId = 1;
    const sensorNumber = -1;
    digitalInputCardsDeepCopy.forEach((digitalSensor) => {
      if (digitalSensor.formId >= formId) {
        formId = digitalSensor.formId + 1;
      }
    });

    const digitalSensor: DigitalInputCard = {
      formId: formId,
      sensorNumber: sensorNumber,
      digitalSensorId: null,
      attributeStateCode: "",
      reportingDescription: "",
    };
    digitalInputCardsDeepCopy.push(digitalSensor);
    setDigitalInputCards(digitalInputCardsDeepCopy);
  }

  // Updates a digital input sensor on the Digital Page.
  function handleUpdateDigitalInput(
    formId: number,
    sensorNumber: number,
    digitalSensorId: number | null,
    attributeStateCode: string,
    reportingDescription: string
  ): void {
    const digitalInputCardsDeepCopy = deepCopy(digitalInputCards);
    const digitalSensor = digitalInputCardsDeepCopy.find((digitalSensor) => digitalSensor.formId === formId);
    let newReportingDescription: string = "";
    if (digitalSensor !== undefined) {
      // Clear the reporting description if the sensor type is changed.
      if (digitalSensor.digitalSensorId === digitalSensorId) {
        newReportingDescription = reportingDescription;
      }
      digitalSensor.sensorNumber = sensorNumber;
      digitalSensor.digitalSensorId = digitalSensorId;
      digitalSensor.attributeStateCode = attributeStateCode;
      digitalSensor.reportingDescription = newReportingDescription;
    }
    setDigitalInputCards(digitalInputCardsDeepCopy);

    // Reflect the digital sensor card changes in the digital sensor map, adding the sensor if it is new.
    // NOTE: Digital sensors are not being added whenever a new card is created because a digitalSensorId is needed.
    updateDigitalInput(formId, sensorNumber, digitalSensorId, attributeStateCode, newReportingDescription);
  }

  // Deletes a digital input sensor on the Digital Input Page.
  function handleDeleteDigitalInput(formId: number, sensorNumber: number, attributeStateCode: string): void {
    const digitalInputCardsDeepCopy = deepCopy(digitalInputCards);
    const selectedDigitalSensorIndex = digitalInputCardsDeepCopy.findIndex(
      (digitalSensor) => digitalSensor.formId === formId
    );
    if (selectedDigitalSensorIndex >= 0) {
      digitalInputCardsDeepCopy.splice(selectedDigitalSensorIndex, 1);
      setDigitalInputCards(digitalInputCardsDeepCopy);
    }

    // Reflect the deletion of the sensor card changes in the digital sensor map.
    updateDigitalInput(formId, sensorNumber, null, attributeStateCode, "");
  }

  // Adds a new digital output card to the digital output page.
  function handleCreateDigitalOutput(): void {
    const digitalOutputCardsDeepCopy = deepCopy(digitalOutputCards);
    let formId = 1;
    const position = -1;
    digitalOutputCardsDeepCopy.forEach((digitalSensor) => {
      if (digitalSensor.formId >= formId) {
        formId = digitalSensor.formId + 1;
      }
    });

    const digitalSensor: DigitalOutputCard = {
      formId: formId,
      position: position,
      operationLabel: "",
      trueLabel: "",
      falseLabel: "",
    };
    digitalOutputCardsDeepCopy.push(digitalSensor);
    setDigitalOutputCards(digitalOutputCardsDeepCopy);
  }

  // Updates a digital output sensor on the digital output page.
  function handleUpdateDigitalOutput(
    formId: number,
    position: number,
    operationLabel: string,
    trueLabel: string,
    falseLabel: string
  ): void {
    const digitalOutputCardsDeepCopy = deepCopy(digitalOutputCards);
    const digitalSensor = digitalOutputCardsDeepCopy.find((digitalSensor) => digitalSensor.formId === formId);
    if (digitalSensor !== undefined) {
      digitalSensor.position = position;
      digitalSensor.operationLabel = operationLabel;
      digitalSensor.trueLabel = trueLabel;
      digitalSensor.falseLabel = falseLabel;
    }
    setDigitalOutputCards(digitalOutputCardsDeepCopy);
  }

  // Deletes a digital output sensor on the digital output Page.
  function handleDeleteDigitalOutput(formId: number): void {
    const digitalOutputCardsDeepCopy = deepCopy(digitalOutputCards);
    const selectedDigitalSensorIndex = digitalOutputCardsDeepCopy.findIndex(
      (digitalSensor) => digitalSensor.formId === formId
    );
    if (selectedDigitalSensorIndex >= 0) {
      digitalOutputCardsDeepCopy.splice(selectedDigitalSensorIndex, 1);
      setDigitalOutputCards(digitalOutputCardsDeepCopy);
    }
  }

  // Updates the device type and clears related configs.
  function handleUpdateDeviceType(deviceType: string): void {
    setDeviceType(deviceType);

    // Clear out analog, digital and modbus configuration when we update the device type to ensure we are not
    // misconfiguring a device
    setModbusMedium("Not used");
    setControllerIndex(-1);
    setModbusSensorId(-1);
    setModbusSensorPosition(-1);
    setAnalogSensorMap({});
    setAnalogInputCards([]);
    setDigitalSensorMap({});
    setDigitalInputCards([]);
    setDigitalOutputCards([]);
  }

  // Validates the asset's settings.
  function assetIsValid(): boolean {
    let isAssetValid = false;

    // Get all selected applications for sensors so we can determine if two sensors are trying to report the same data.
    let duplicateAnalogSensorMapping = false;
    let sensorSelectedPositions: number[] = [];
    const sensorSelectedAttributes: number[] = [];
    Object.keys(analogSensorMap).forEach((analogSensorMapKey) => {
      if (sensorSelectedAttributes.includes(analogSensorMap[analogSensorMapKey].mappedAttributeId)) {
        duplicateAnalogSensorMapping = true;
      } else {
        sensorSelectedAttributes.push(analogSensorMap[analogSensorMapKey].mappedAttributeId);
      }

      // Check for duplicate analog input sensor positions.
      let sensorPosition: number = 0;
      const sensorPositionMatch = analogSensorMapKey.match(/AI(\d+)Engineering/i);
      if (sensorPositionMatch) {
        sensorPosition = parseInt(sensorPositionMatch[1], 10);
      }
      if (sensorPosition > 0) {
        if (sensorSelectedPositions.includes(sensorPosition)) {
          duplicateAnalogSensorMapping = true;
        } else {
          sensorSelectedPositions.push(sensorPosition);
        }
      }
    });

    // Get all selected applications for sensors so we can determine if two sensors are trying to report the same data.
    sensorSelectedPositions = [];
    let duplicateDigitalSensorMapping = false;
    const sensorSelectedReportingDescriptions: string[] = [];
    Object.keys(digitalSensorMap).forEach((digitalSensorMapKey) => {
      if (sensorSelectedReportingDescriptions.includes(digitalSensorMap[digitalSensorMapKey].reportingDescription)) {
        duplicateDigitalSensorMapping = true;
      } else {
        sensorSelectedReportingDescriptions.push(digitalSensorMap[digitalSensorMapKey].reportingDescription);
      }

      // Check for duplicate digital input sensor positions.
      let sensorPosition: number = 0;
      const sensorPositionMatch = digitalSensorMapKey.match(/DI(\d+)State/i);
      if (sensorPositionMatch) {
        sensorPosition = parseInt(sensorPositionMatch[1], 10);
      }
      if (sensorPosition > 0) {
        if (sensorSelectedPositions.includes(sensorPosition)) {
          duplicateDigitalSensorMapping = true;
        } else {
          sensorSelectedPositions.push(sensorPosition);
        }
      }
    });

    // Check for incomplete analog input sensor cards.
    let incompleteAnalogInputMapping = false;
    if (analogInputCards.find((card) => card.analogSensorId === null || card.attributeId === null) !== undefined) {
      incompleteAnalogInputMapping = true;
    }

    // Check for incomplete digital input sensor cards.
    let incompleteDigitalInputMapping = false;
    if (
      digitalInputCards.find((card) => card.digitalSensorId === null || card.reportingDescription === "") !== undefined
    ) {
      incompleteDigitalInputMapping = true;
    }

    // Check for incomplete digital output sensor cards.
    let incompleteDigitalOutputMapping = false;
    if (
      digitalOutputCards.find(
        (card) => card.operationLabel.length === 0 || card.trueLabel.length === 0 || card.falseLabel.length === 0
      ) !== undefined
    ) {
      incompleteDigitalOutputMapping = true;
    }

    // Check that digital outputs do not share the same operation label.
    const digitalOutputOperationLabels: string[] = [];
    let duplicateDigitalOutputOperationLabel = false;
    digitalOutputCards.forEach((card) => {
      if (digitalOutputOperationLabels.includes(card.operationLabel)) {
        duplicateDigitalOutputOperationLabel = true;
      } else {
        digitalOutputOperationLabels.push(card.operationLabel);
      }
    });

    // Validation checks and error messages.
    if (name.trim().length < MIN_ASSET_NAME_LENGTH || name.trim().length > MAX_ASSET_NAME_LENGTH) {
      setErrorMessage(
        `The asset name must be between ${MIN_ASSET_NAME_LENGTH} and ${MAX_ASSET_NAME_LENGTH} characters long ` +
          "(see 'Asset Details' step)."
      );
    } else if (nickname.trim().length > MAX_ASSET_FIELDS_LENGTH) {
      setErrorMessage(
        `The asset nickname must be a maximum of ${MAX_ASSET_FIELDS_LENGTH} characters long (see 'Asset Details' step).`
      );
    } else if (assetTemplateId === null) {
      setErrorMessage("You must select a template for this asset (see 'Asset Details' step).");
    } else if (assetMapIconId === null) {
      setErrorMessage("You must select a map icon for this asset (see 'Asset Details' step).");
    } else if (dataCollectionId === -1) {
      setErrorMessage("You must select how this asset collects data (see 'Asset Details' step).");
    } else if (
      dataCollectionId === DATA_COLLECTION_MONITORING_DEVICE &&
      (deviceType === null || deviceType === undefined || deviceType.length === 0)
    ) {
      setErrorMessage("You must select an option for the monitoring device model (see 'Asset Details' step).");
    } else if (
      dataCollectionId === DATA_COLLECTION_MONITORING_DEVICE &&
      deviceType !== null &&
      deviceIdentifier === null
    ) {
      setErrorMessage(
        "When a monitoring device type is selected, an identifier is also required (see 'Asset Details' step)."
      );
    } else if (dataCollectionId === DATA_COLLECTION_OTHER_ASSET && gatewayAssetId === null) {
      setErrorMessage("You must provide the name of the asset providing data (see 'Asset Details' step).");
    } else if (productType.trim().length === 0) {
      setErrorMessage("You must select an option for the product type (see 'Product' step).");
    } else if (
      productModel.trim().length < MIN_ASSET_FIELDS_LENGTH ||
      productModel.trim().length > MAX_ASSET_FIELDS_LENGTH
    ) {
      setErrorMessage(
        `The product part number must be between ${MIN_ASSET_FIELDS_LENGTH} and ` +
          `${MAX_ASSET_FIELDS_LENGTH} characters long (see 'Product' step).`
      );
    } else if (
      productManufacturer === "OTHER" &&
      (otherProductManufacturer.trim().length < MIN_ASSET_FIELDS_LENGTH ||
        otherProductManufacturer.trim().length > MAX_ASSET_FIELDS_LENGTH)
    ) {
      setErrorMessage(
        `The product manufacturer must be between ${MIN_ASSET_FIELDS_LENGTH} and ` +
          `${MAX_ASSET_FIELDS_LENGTH} characters long (see 'Product' step).`
      );
    } else if (
      productIdentifier.trim().length < MIN_ASSET_FIELDS_LENGTH ||
      productIdentifier.trim().length > MAX_ASSET_FIELDS_LENGTH
    ) {
      setErrorMessage(
        `The product serial number must be between ${MIN_ASSET_FIELDS_LENGTH} and ` +
          `${MAX_ASSET_FIELDS_LENGTH} characters long (see 'Product' step).`
      );
    } else if (productManufacturer.trim().length === 0) {
      setErrorMessage("You must select an option for the product manufacturer (see 'Product' step).");
    } else if (modbusMedium === "Controller" && controllerIndex < 0) {
      setErrorMessage("You must select an option for the controller (see 'Modbus' step).");
    } else if (modbusMedium === "Sensor" && modbusSensorId < 0) {
      setErrorMessage("You must select an option for the modbus sensor (see 'Modbus' step).");
    } else if (modbusMedium === "Sensor" && modbusSensorPosition < 0) {
      setErrorMessage("You must select an option for the modbus sensor position (see 'Modbus' step).");
    } else if (incompleteAnalogInputMapping) {
      setErrorMessage(
        "You must select a sensor type and reporting description for every analog input sensor (see 'Analog Input' step)."
      );
    } else if (incompleteDigitalInputMapping) {
      setErrorMessage(
        "You must select a sensor type and reporting description for every digital input sensor (see 'Digital Input' step)."
      );
    } else if (incompleteDigitalOutputMapping) {
      setErrorMessage("You must fill out all labels for digital output sensors (see 'Digital Output' step).");
    } else if (duplicateDigitalOutputOperationLabel) {
      setErrorMessage(
        "Two or more digital output sensors cannot have the same operation label (see 'Digital Output' step)."
      );
    } else if (duplicateAnalogSensorMapping) {
      setErrorMessage(
        "Two or more analog input sensors cannot report the same type of data (see 'Analog Input' step)."
      );
    } else if (duplicateDigitalSensorMapping) {
      setErrorMessage(
        "Two or more digital input sensors cannot report the same type of data (see 'Digital Input' step)."
      );
    } else if (serviceTime !== "" && Number.isNaN(parseFloat(serviceTime))) {
      setErrorMessage("Hours since last serviced must be a valid number (see 'Data' step).");
    } else if (Number.isNaN(parseFloat(latitude)) || Number.isNaN(parseFloat(longitude))) {
      setErrorMessage("GPS coordinates must be valid decimal numbers (see 'Data' step).");
    } else {
      setErrorMessage("");
      isAssetValid = true;
    }
    return isAssetValid;
  }

  // Creates an asset.
  async function createAsset(): Promise<void> {
    setShowConfirmTemplateConfig(false);
    setShowConfirmSensorConfig(false);
    if (assetIsValid()) {
      // Get the selected product manufacturer or the write in.
      let currentProductManufacturer;
      if (productManufacturer === "OTHER") {
        currentProductManufacturer = otherProductManufacturer;
      } else {
        currentProductManufacturer = productManufacturer;
      }

      // Check if a controller was selected.
      let controllerModel: string | null = null;
      let controllerManufacturerCode: string | null = null;
      if (controllerIndex >= 0) {
        const controller = props.controllers.find((controller) => controller.controllerId === controllerIndex);
        if (controller !== null && controller !== undefined) {
          controllerModel = controller.controllerModel;
          controllerManufacturerCode = controller.manufacturerCompanyCode;
        }
      }

      // Check if a modbus was selected.
      let currentModbusSensorId: number | null = null;
      if (modbusSensorId >= 0) {
        currentModbusSensorId = modbusSensorId;
      }

      let currentModbusSensorSlaveId: number | null = null;
      if (modbusSensorPosition >= 0) {
        currentModbusSensorSlaveId = modbusSensorPosition;
      }

      // Set GPS coordinates if manually setting location.
      let currentLatitude = null;
      let currentLongitude = null;
      if (updateLocationManually) {
        currentLatitude = parseFloat(latitude);
        currentLongitude = parseFloat(longitude);
      }

      const requestBody: AssetRequestBody = {
        assetName: name.trim(),
        assetNickname: nickname.trim(),
        assetTemplateId: assetTemplateId as number, // assetIsValid function prevents this from being null.
        assetMapIconId: assetMapIconId,
        productType: productType.trim(),
        productModel: productModel.trim(),
        productIdentifier: productIdentifier.trim(),
        productManufacturer: currentProductManufacturer.trim(),
        controllerModel: controllerModel,
        controllerManufacturerCode: controllerManufacturerCode,
        deviceType: deviceType,
        deviceIdentifier: deviceIdentifier,
        updateTemplate: true,
        updateSensors: true,
        analogSensors: [],
        digitalSensors: [],
        digitalOutputs: digitalOutputCards,
        serviceHoursAttributeCode: serviceAttribute,
        runtimeSinceLastServiced: serviceTime !== "" ? parseFloat(serviceTime) : null,
        gpsLatitude: currentLatitude,
        gpsLongitude: currentLongitude,
        isGateway: isGateway,
        hideGatewayData: hideGatewayData,
        gatewayAssetId: gatewayAssetId,
        modbusSensorId: currentModbusSensorId,
        modbusSensorSlaveId: currentModbusSensorSlaveId,
      };

      // Add analog input settings to the request body.
      Object.keys(analogSensorMap).forEach((analogSensorKey) =>
        requestBody.analogSensors.push(analogSensorMap[analogSensorKey])
      );

      // Add digital input settings to the request body.
      Object.keys(digitalSensorMap).forEach((digitalSensorKey) =>
        requestBody.digitalSensors.push(digitalSensorMap[digitalSensorKey])
      );

      // Set runtimeSinceLastServiced to null if updateServiceTime is false.
      if (!updateServiceTimeManually) {
        requestBody.runtimeSinceLastServiced = null;
      }

      setErrorMessage("");
      setLoading(true);
      const [response, responseBody] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/asset`,
        "POST",
        requestBody
      )) as [Response, PostResponseBody];
      setLoading(false);

      if (response.ok && responseBody) {
        const newAsset = {
          assetId: responseBody.assetId,
          name: name,
          nickname: nickname,
          assetTemplateId: assetTemplateId,
          assetMapIconId: assetMapIconId,
          productType: productType,
          productModel: productModel,
          productIdentifier: productIdentifier,
          productManufacturer: currentProductManufacturer,
          controllerModel: controllerModel,
          controllerManufacturer: controllerManufacturerCode,
          deviceType: deviceType,
          deviceIdentifier: deviceIdentifier,
          isRented: false,
          analogInputs: requestBody.analogSensors,
          digitalInputs: requestBody.digitalSensors,
          digitalOutputs: requestBody.digitalOutputs,
          gpsLatitude: currentLatitude !== null ? String(currentLatitude) : "",
          gpsLongitude: currentLongitude !== null ? String(currentLongitude) : "",
          isGateway: isGateway,
          hideGatewayData: hideGatewayData,
          gatewayAssetId: gatewayAssetId,
          modbusSensorId: currentModbusSensorId,
          modbusSensorSlaveId: currentModbusSensorSlaveId,
        };

        props.onAction({
          type: ASSET_TYPES.CREATE_ASSET,
          payload: {
            asset: newAsset,
          },
        });

        discardChanges();
      } else {
        setErrorMessage(await getApiError(response, "Unable to create asset."));
      }
    }
  }

  // Update an asset.
  async function editAsset(updateTemplate: boolean, updateSensors: boolean, assetId: number): Promise<void> {
    setShowConfirmTemplateConfig(false);
    setShowConfirmSensorConfig(false);
    if (assetIsValid()) {
      // Get the selected product manufacturer or the write in.
      let currentProductManufacturer;
      if (productManufacturer === "OTHER") {
        currentProductManufacturer = otherProductManufacturer;
      } else {
        currentProductManufacturer = productManufacturer;
      }

      // Check if a controller was selected.
      let controllerModel: string | null = null;
      let controllerManufacturerCode: string | null = null;
      if (controllerIndex >= 0) {
        const controller = props.controllers.find((controller) => controller.controllerId === controllerIndex);
        if (controller !== null && controller !== undefined) {
          controllerModel = controller.controllerModel;
          controllerManufacturerCode = controller.manufacturerCompanyCode;
        }
      }

      // Check if a modbus was selected.
      let currentModbusSensorId: number | null = null;
      if (modbusSensorId >= 0) {
        currentModbusSensorId = modbusSensorId;
      }

      let currentModbusSensorSlaveId: number | null = null;
      if (modbusSensorPosition >= 0) {
        currentModbusSensorSlaveId = modbusSensorPosition;
      }

      // Set GPS coordinates if manually setting location.
      let currentLatitude = null;
      let currentLongitude = null;
      if (updateLocationManually) {
        currentLatitude = parseFloat(latitude);
        currentLongitude = parseFloat(longitude);
      }

      const requestBody: AssetRequestBody = {
        assetName: name.trim(),
        assetNickname: nickname.trim(),
        assetTemplateId: assetTemplateId as number, // assetIsValid function prevents this from being null.
        assetMapIconId: assetMapIconId,
        productType: productType.trim(),
        productModel: productModel.trim(),
        productIdentifier: productIdentifier.trim(),
        productManufacturer: currentProductManufacturer.trim(),
        controllerModel: controllerModel,
        controllerManufacturerCode: controllerManufacturerCode,
        deviceType: deviceType,
        deviceIdentifier: deviceIdentifier,
        updateTemplate: updateTemplate || !lastConfigSuccessful,
        updateSensors: updateSensors || !lastConfigSuccessful,
        analogSensors: [],
        digitalSensors: [],
        digitalOutputs: digitalOutputCards,
        updateServiceHours: updateServiceTimeManually,
        serviceHoursAttributeCode: serviceAttribute,
        runtimeSinceLastServiced: serviceTime !== "" ? parseFloat(serviceTime) : null,
        gpsLatitude: currentLatitude,
        gpsLongitude: currentLongitude,
        isGateway: isGateway,
        hideGatewayData: hideGatewayData,
        gatewayAssetId: gatewayAssetId,
        modbusSensorId: currentModbusSensorId,
        modbusSensorSlaveId: currentModbusSensorSlaveId,
      };

      // Add analog sensor settings to the request body.
      Object.keys(analogSensorMap).forEach((analogSensorKey) =>
        requestBody.analogSensors.push(analogSensorMap[analogSensorKey])
      );

      // Add digital sensor settings to the request body.
      Object.keys(digitalSensorMap).forEach((digitalSensorKey) =>
        requestBody.digitalSensors.push(digitalSensorMap[digitalSensorKey])
      );

      setErrorMessage("");
      setLoading(true);
      const [response] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/asset/${assetId}`,
        "PUT",
        requestBody
      )) as [Response, PutResponseBody];
      setLoading(false);

      if (response.ok) {
        const updatedAsset = {
          assetId: props.assetId,
          name: name,
          nickname: nickname,
          assetTemplateId: assetTemplateId,
          assetMapIconId: assetMapIconId,
          productType: productType,
          productModel: productModel,
          productIdentifier: productIdentifier,
          productManufacturer: currentProductManufacturer,
          controllerModel: controllerModel,
          controllerManufacturer: controllerManufacturerCode,
          deviceType: deviceType,
          deviceIdentifier: deviceIdentifier,
          isRented: isRented,
          analogInputs: requestBody.analogSensors,
          digitalInputs: requestBody.digitalSensors,
          digitalOutputs: requestBody.digitalOutputs,
          gpsLatitude: currentLatitude !== null ? String(currentLatitude) : "",
          gpsLongitude: currentLongitude !== null ? String(currentLongitude) : "",
          isGateway: isGateway,
          hideGatewayData: hideGatewayData,
          gatewayAssetId: gatewayAssetId,
          modbusSensorId: currentModbusSensorId,
          modbusSensorSlaveId: currentModbusSensorSlaveId,
        };
        props.onClose();
        props.onAction({
          type: ASSET_TYPES.UPDATE_ASSET,
          payload: {
            asset: updatedAsset,
          },
        });

        discardChanges();
      } else {
        setErrorMessage(await getApiError(response, "Unable to update asset."));
      }
    }
  }

  // Update an asset's rental fields.
  async function updateAssetRental(nickname: string, assetMapIconId: number | null, assetId: number): Promise<void> {
    if (assetMapIconId === null) {
      setErrorMessage("You must select a map icon for this asset.");
    } else {
      const requestBody = {
        assetNickname: nickname,
        assetMapIconId: assetMapIconId,
      };

      setLoading(true);
      const [response] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/asset/${assetId}/rental`,
        "PUT",
        requestBody
      )) as [Response, PutResponseBody];
      setLoading(false);

      if (response.ok) {
        setErrorMessage("");
        props.onClose();

        props.onAction({
          type: ASSET_TYPES.UPDATE_ASSET_NICKNAME,
          payload: {
            assetId: props.assetId,
            nickname: nickname,
          },
        });

        discardChanges();
      } else {
        setErrorMessage(await getApiError(response, "Unable to update asset."));
      }
    }
  }

  // Deletes an asset.
  async function deleteAsset(assetId: number): Promise<void> {
    setErrorMessage("");
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}`,
      "DELETE",
      null
    )) as [Response, DeleteResponseBody];
    setLoading(false);

    if (response.ok) {
      discardChanges();
      props.onAction({
        type: ASSET_TYPES.DELETE_ASSET,
        payload: {
          assetId: assetId,
        },
      });
    } else {
      setShowConfirmDelete(false);
      setErrorMessage(await getApiError(response, "Unable to delete asset."));
    }
  }

  // Saves changes to an asset. If a change requires device configuration,
  // confirm configuration with the user before saving.
  async function saveChanges(): Promise<void> {
    const templateUpdateRequired =
      (deviceType !== null || gatewayAssetId !== null) &&
      deviceType !== PULSE_DEVICE_TYPE &&
      (controllerHasChanged() ||
        modbusSensorHasChanged() ||
        deviceType !== previousAsset.deviceType ||
        deviceIdentifier !== previousAsset.deviceIdentifier);

    // Check if sensor update is required.
    const sensorUpdateRequired = sensorsHaveChanged() || templateUpdateRequired;

    if (templateUpdateRequired || sensorUpdateRequired) {
      setShowConfirmTemplateConfig(templateUpdateRequired);
      setShowConfirmSensorConfig(sensorUpdateRequired);
    } else {
      if (props.isCreatingNewAsset) {
        void createAsset();
      } else if (isRented) {
        void updateAssetRental(nickname, assetMapIconId, props.assetId);
      } else {
        void editAsset(templateUpdateRequired, sensorUpdateRequired, props.assetId);
      }
    }
    setShowConfirmExit(false);
  }

  // Takes an array of sensors and sorts them based on a key in the sensor object.
  function sortSensorsBySortKey<T>(sensors: T[], sortKey: (item: T) => string): T[] {
    return [...sensors].sort((a, b) => {
      const keyA = sortKey(a);
      const keyB = sortKey(b);
      if (keyA < keyB) return -1;
      if (keyA > keyB) return 1;
      return 0;
    });
  }

  // Takes a previous and current array of sensors to compare, and returns whether they have changed.
  function getSensorsHaveChanged<T>(previous: T[], current: T[], sortKey: (item: T) => string): boolean {
    const sortedPrevious = sortSensorsBySortKey(previous, sortKey);
    const sortedCurrent = sortSensorsBySortKey(current, sortKey);
    return JSON.stringify(sortedPrevious) !== JSON.stringify(sortedCurrent);
  }

  // Compares current sensors with previous sensors to see if any changes have occurred.
  function sensorsHaveChanged(): boolean {
    const previousAnalogInputs: AnalogInputSelection[] = previousAsset.analogInputs.map((analogInput) => ({
      analogSensorId: analogInput.analogSensorId,
      attributeCode: analogInput.attributeCode,
      attributeConnectedCode: analogInput.attributeConnectedCode,
      attributeModeCode: analogInput.attributeModeCode,
      attributeMinCode: analogInput.attributeMinCode,
      attributeSpanCode: analogInput.attributeSpanCode,
      mappedAttributeId: analogInput.mappedAttributeId,
      mappedAttributeConnectedId: analogInput.mappedAttributeConnectedId,
    }));
    const currentAnalogInputs: AnalogInputSelection[] = Object.values(analogSensorMap).map((analogInput) => ({
      analogSensorId: analogInput.analogSensorId,
      attributeCode: analogInput.attributeCode,
      attributeConnectedCode: analogInput.attributeConnectedCode,
      attributeModeCode: analogInput.attributeModeCode,
      attributeMinCode: analogInput.attributeMinCode,
      attributeSpanCode: analogInput.attributeSpanCode,
      mappedAttributeId: analogInput.mappedAttributeId,
      mappedAttributeConnectedId: analogInput.mappedAttributeConnectedId,
    }));
    const previousDigitalInputs: DigitalInputSelection[] = previousAsset.digitalInputs.map((digitalInput) => ({
      digitalSensorId: digitalInput.digitalSensorId,
      attributeStateCode: digitalInput.attributeStateCode,
      attributeFlowCode: digitalInput.attributeFlowCode,
      attributeAccumulationCode: digitalInput.attributeAccumulationCode,
      attributeModeCode: digitalInput.attributeModeCode,
      attributeKFactorCode: digitalInput.attributeKFactorCode,
      attributeOffsetCode: digitalInput.attributeOffsetCode,
      attributeDebounceCode: digitalInput.attributeDebounceCode,
      attributeUnitsPerPulseCode: digitalInput.attributeUnitsPerPulseCode,
      mappedAttributeStateId: digitalInput.mappedAttributeStateId,
      mappedAttributeFlowId: digitalInput.mappedAttributeFlowId,
      mappedAttributeAccumulationId: digitalInput.mappedAttributeAccumulationId,
      reportingDescription: digitalInput.reportingDescription,
    }));
    const currentDigitalInputs: DigitalInputSelection[] = Object.values(digitalSensorMap).map(
      (digitalSensorMapValue) => ({
        digitalSensorId: digitalSensorMapValue.digitalSensorId,
        attributeStateCode: digitalSensorMapValue.attributeStateCode,
        attributeFlowCode: digitalSensorMapValue.attributeFlowCode,
        attributeAccumulationCode: digitalSensorMapValue.attributeAccumulationCode,
        attributeModeCode: digitalSensorMapValue.attributeModeCode,
        attributeKFactorCode: digitalSensorMapValue.attributeKFactorCode,
        attributeOffsetCode: digitalSensorMapValue.attributeOffsetCode,
        attributeDebounceCode: digitalSensorMapValue.attributeDebounceCode,
        attributeUnitsPerPulseCode: digitalSensorMapValue.attributeUnitsPerPulseCode,
        mappedAttributeStateId: digitalSensorMapValue.mappedAttributeStateId,
        mappedAttributeFlowId: digitalSensorMapValue.mappedAttributeFlowId,
        mappedAttributeAccumulationId: digitalSensorMapValue.mappedAttributeAccumulationId,
        reportingDescription: digitalSensorMapValue.reportingDescription,
      })
    );
    const previousDigitalOutputs: DigitalOutputSelection[] = previousAsset.digitalOutputs.map((digitalOutput) => ({
      position: digitalOutput.position,
      operationLabel: digitalOutput.operationLabel,
      trueLabel: digitalOutput.trueLabel,
      falseLabel: digitalOutput.falseLabel,
    }));
    const currentDigitalOutputs: DigitalOutputSelection[] = digitalOutputCards.map((digitalOutput) => ({
      position: digitalOutput.position,
      operationLabel: digitalOutput.operationLabel,
      trueLabel: digitalOutput.trueLabel,
      falseLabel: digitalOutput.falseLabel,
    }));
    const analogInputSensorsChanged = getSensorsHaveChanged(
      previousAnalogInputs,
      currentAnalogInputs,
      (analogInput) => analogInput.attributeCode
    );
    const digitalInputSensorsChanged = getSensorsHaveChanged(
      previousDigitalInputs,
      currentDigitalInputs,
      (digitalInput) => digitalInput.attributeStateCode
    );
    const digitalOutputSensorsChanged = getSensorsHaveChanged(
      previousDigitalOutputs,
      currentDigitalOutputs,
      (digitalOutput) => digitalOutput.operationLabel
    );
    return analogInputSensorsChanged || digitalInputSensorsChanged || digitalOutputSensorsChanged;
  }

  // Compare the current controller with the previous controller to see if any changes have occurred.
  function controllerHasChanged(): boolean {
    let controllerModel = null;
    let controllerManufacturer = null;

    // Get the current controller model and controller manufacturer.
    if (controllerIndex >= 0) {
      const currentController = props.controllers.find((controller) => controller.controllerId === controllerIndex);
      if (currentController !== null && currentController !== undefined) {
        controllerModel = currentController.controllerModel;
        controllerManufacturer = currentController.manufacturerCompanyCode;
      }
    }

    return (
      controllerModel !== previousAsset.controllerModel ||
      controllerManufacturer !== previousAsset.controllerManufacturer
    );
  }

  // Compare the current modbus sensor with the previous modbus sensor to see if any changes have occurred.
  function modbusSensorHasChanged(): boolean {
    return (
      modbusSensorId !== previousAsset.modbusSensorId || modbusSensorPosition !== previousAsset.modbusSensorSlaveId
    );
  }

  // Exit modal if no changes have been made. Otherwise, prompt the user.
  function exitModal(): void {
    if (
      !sensorsHaveChanged() &&
      !controllerHasChanged() &&
      name === previousAsset.name &&
      nickname === previousAsset.nickname &&
      assetTemplateId === previousAsset.assetTemplateId &&
      assetMapIconId === previousAsset.assetMapIconId &&
      dataCollectionId === previousDataCollectionId &&
      deviceType === previousAsset.deviceType &&
      deviceIdentifier === previousAsset.deviceIdentifier &&
      isGateway === previousAsset.isGateway &&
      gatewayAssetId === previousAsset.gatewayAssetId &&
      hideGatewayData === previousAsset.hideGatewayData &&
      productType === previousAsset.productType &&
      productModel === previousAsset.productModel &&
      productIdentifier === previousAsset.productIdentifier &&
      modbusSensorId === previousAsset.modbusSensorId &&
      modbusSensorPosition === previousAsset.modbusSensorSlaveId &&
      serviceAttribute === previousServiceAttribute &&
      serviceTime === previousServiceTime &&
      latitude === previousLatitude &&
      longitude === previousLongitude &&
      (productManufacturer === previousAsset.productManufacturer ||
        (productManufacturer === "OTHER" && otherProductManufacturer === previousAsset.productManufacturer))
    ) {
      // No changes have been made, so the user can safely exit the modal.
      discardChanges();
    } else {
      // Give the user a chance to save their unsaved changes.
      setShowConfirmExit(true);
    }
  }

  // Exit without saving changes.
  function discardChanges(): void {
    setName("");
    setNickname("");
    setAssetTemplateId(null);
    setAssetMapIconId(null);
    setDataCollectionId(-1);
    setDeviceType(null);
    setDeviceIdentifier(null);
    setIsGateway(false);
    setGatewayAssetId(null);
    setHideGatewayData(false);
    setProductType("");
    setProductManufacturer("");
    setOtherProductManufacturer("");
    setProductModel("");
    setProductIdentifier("");
    setControllerIndex(-1);
    setModbusSensorId(-1);
    setModbusSensorPosition(-1);
    setAnalogSensorMap({});
    setDigitalSensorMap({});
    setServiceAttribute(null);
    setServiceTime("");
    setLatitude(String(GOOGLE_MAPS_DEFAULT_LAT));
    setLongitude(String(GOOGLE_MAPS_DEFAULT_LNG));

    setPreviousAsset(initialAsset);
    setPreviousDataCollectionId(-1);
    setPreviousServiceAttribute(null);
    setPreviousServiceTime("");
    setPreviousLatitude(String(GOOGLE_MAPS_DEFAULT_LAT));
    setPreviousLongitude(String(GOOGLE_MAPS_DEFAULT_LNG));

    setActivePageNumber(1);
    setErrorMessage("");
    setGatewayAssetName("");
    setGatewayDeviceType(null);
    setModbusMedium("Not used");
    setModbusInfoInUse(null);
    setControllerInUse(null);
    setAnalogInputsInUse(null);
    setDigitalInputsInUse(null);
    setDigitalOutputsInUse(null);
    setAnalogInputCards([]);
    setDigitalInputCards([]);
    setDigitalOutputCards([]);
    setUpdateServiceTimeManually(false);
    setUpdateLocationManually(false);
    setIsRented(false);
    setIsMigrating(false);
    setLastConfigSuccessful(true);
    setLastConfigAttemptUtc("");
    setShowConfirmDelete(false);
    setShowConfirmExit(false);

    props.onClose();
  }

  return (
    <div data-test="asset-crud-wizard-container">
      <Spinner loading={loading} />

      <Modal
        show={props.showModal}
        onHide={() => exitModal()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="xl"
        animation
      >
        <ModalHeader>
          <div className="container">
            <div className="row">
              <h5 className="font-weight-bold">{props.isCreatingNewAsset ? "Create Asset" : "Update Asset"}</h5>
            </div>
            <div className="row">
              <Breadcrumbs
                breadcrumbs={breadcrumbs}
                activePageNumber={activePageNumber}
                onClick={(pageNumber: number) => setActivePageNumber(pageNumber)}
              />
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          {activePageTitle === "Asset Details" && (
            <DetailsPage
              isCreatingNewAsset={props.isCreatingNewAsset}
              name={name}
              nickname={nickname}
              templateId={assetTemplateId}
              templates={props.templates}
              assetMapIconId={assetMapIconId}
              assetMapIcons={props.assetMapIcons}
              dataCollectionId={dataCollectionId}
              deviceTypes={props.deviceTypes}
              deviceType={deviceType || ""}
              deviceIdentifier={deviceIdentifier || ""}
              monitoringDevices={props.monitoringDevices}
              gatewayAssets={props.gatewayAssets}
              isGateway={isGateway}
              gatewayAssetName={gatewayAssetName}
              hideGatewayData={hideGatewayData}
              disabled={isRented || !formIsEditable()}
              formIsEditable={formIsEditable()}
              onChangeName={(name) => setName(name)}
              onChangeNickname={(nickname) => setNickname(nickname)}
              onChangeTemplateId={(templateId) => setAssetTemplateId(templateId)}
              onChangeMapIconId={(mapIconId) => setAssetMapIconId(mapIconId)}
              onChangeDataCollectionId={(dataCollectionId) => handleDataCollectionIdChange(dataCollectionId)}
              onChangeDeviceType={(deviceType) => handleUpdateDeviceType(deviceType)}
              onChangeDeviceIdentifier={(deviceIdentifier) => setDeviceIdentifier(deviceIdentifier)}
              onChangeHideGatewayData={() => setHideGatewayData((prev) => !prev)}
              onChangeIsGateway={(isGateway) => handleIsGateway(isGateway)}
              onSearchGatewayAssets={(filter) => searchGatewayAssets(filter)}
            />
          )}

          {activePageTitle === "Product" && (
            <ProductPage
              productType={productType}
              productTypes={props.productTypes}
              productManufacturer={productManufacturer}
              otherProductManufacturer={otherProductManufacturer}
              productModel={productModel}
              productIdentifier={productIdentifier}
              disabled={isRented || !formIsEditable()}
              onChangeProductType={(productType: string) => setProductType(productType)}
              onChangeProductManufacturer={(productManufacturer: string) => setProductManufacturer(productManufacturer)}
              onChangeOtherProductManufacturer={(otherProductManufacturer: string) =>
                setOtherProductManufacturer(otherProductManufacturer)
              }
              onChangeProductModel={(productModel: string) => setProductModel(productModel)}
              onChangeProductIdentifier={(productIdentifier: string) => setProductIdentifier(productIdentifier)}
            />
          )}

          {activePageTitle === "Modbus" && (
            <ModbusPage
              disabled={isRented || !formIsEditable()}
              isScadaRtuDeviceType={isScadaRtuDeviceType}
              hasDigitalOutputs={hasDigitalOutputs}
              modbusMedium={modbusMedium}
              controllerIndex={controllerIndex}
              controllers={props.controllers}
              modbusSensorId={modbusSensorId}
              modbusSensors={props.modbusSensors}
              modbusSensorPosition={modbusSensorPosition}
              modbusInfoInUse={modbusInfoInUse}
              controllerInUse={controllerInUse}
              assetName={previousAsset.name !== "" ? previousAsset.name : name}
              onChangeModbusMedium={(modbusMedium) => setModbusMedium(modbusMedium)}
              onChangeControllerIndex={(controllerIndex) => setControllerIndex(controllerIndex)}
              onChangeModbusSensorIndex={(modbusSensorIndex) => setModbusSensorId(modbusSensorIndex)}
              onChangeModbusSensorPosition={(modbusSensorPosition) => setModbusSensorPosition(modbusSensorPosition)}
            />
          )}

          {activePageTitle === "Analog Input" && (
            <AnalogInputPage
              sensorCount={maxAnalogInputCount}
              analogSensorMap={analogSensorMap}
              analogSensors={props.analogSensors}
              analogInputCards={analogInputCards}
              analogInputsInUse={analogInputsInUse}
              disabled={isRented || !formIsEditable()}
              onCreate={() => handleCreateAnalogInput()}
              onChange={(formId, sensorNumber, analogSensorId, attributeId, attributeCode) =>
                handleUpdateAnalogInput(formId, sensorNumber, analogSensorId, attributeId, attributeCode)
              }
              onDelete={(formId, sensorNumber, attributeCode) =>
                handleDeleteAnalogInput(formId, sensorNumber, attributeCode)
              }
            />
          )}

          {activePageTitle === "Digital Input" && (
            <DigitalInputPage
              isRented={isRented}
              sensorCount={maxDigitalInputCount}
              digitalSensorMap={digitalSensorMap}
              digitalSensors={props.digitalSensors}
              digitalInputCards={digitalInputCards}
              digitalInputsInUse={digitalInputsInUse}
              deviceType={deviceType}
              disabled={isRented || !formIsEditable()}
              onCreate={() => handleCreateDigitalInput()}
              onChange={(formId, sensorNumber, digitalSensorId, attributeStateCode, reportingDescription) =>
                handleUpdateDigitalInput(
                  formId,
                  sensorNumber,
                  digitalSensorId,
                  attributeStateCode,
                  reportingDescription
                )
              }
              onDelete={(formId, sensorNumber, attributeStateCode) =>
                handleDeleteDigitalInput(formId, sensorNumber, attributeStateCode)
              }
            />
          )}

          {activePageTitle === "Digital Output" && (
            <DigitalOutputPage
              isRented={isRented}
              isModbusInUse={isModbusInUse}
              isScadaRtuDeviceType={isScadaRtuDeviceType}
              sensorCount={maxDigitalOutputCount}
              digitalOutputCards={digitalOutputCards}
              digitalOutputsInUse={digitalOutputsInUse}
              deviceType={deviceType}
              disabled={isRented || !formIsEditable()}
              onCreate={() => handleCreateDigitalOutput()}
              onChange={(formId, position, operationLabel, trueLabel, falseLabel) =>
                handleUpdateDigitalOutput(formId, position, operationLabel, trueLabel, falseLabel)
              }
              onDelete={(formId) => handleDeleteDigitalOutput(formId)}
            />
          )}

          {activePageTitle === "Data" && (
            <DataPage
              runtimeServiceAttributeMap={props.runtimeServiceAttributeMap}
              serviceAttribute={serviceAttribute}
              serviceTime={serviceTime}
              updateServiceTimeManually={updateServiceTimeManually}
              updateLocationManually={updateLocationManually}
              latitude={latitude}
              longitude={longitude}
              assetMapIcons={props.assetMapIcons}
              assetMapIconId={assetMapIconId}
              disabled={isRented || !formIsEditable()}
              onChangeServiceAttribute={(serviceAttribute) => setServiceAttribute(serviceAttribute)}
              onChangeServiceTime={(serviceTime) => setServiceTime(serviceTime)}
              onChangeUpdateServiceTimeManually={() => setUpdateServiceTimeManually((prev) => !prev)}
              onChangeUpdateLocationManually={() => setUpdateLocationManually((prev) => !prev)}
              onChangeLatitude={(latitude) => setLatitude(latitude)}
              onChangeLongitude={(longitude) => setLongitude(longitude)}
            />
          )}

          {(errorMessage.length > 0 || isRented || isMigrating || !lastConfigSuccessful) && (
            <div className="row">
              <div className="col mt-4 mx-2">
                {isRented && (
                  <Warning message={"You are renting this asset. Your ability to modify this asset is limited."} />
                )}

                {isMigrating && (
                  <Warning
                    message={
                      "This asset has not completed the migration process. New configuration settings will not be " +
                      "applied to the monitoring device hardware to support changes to controllers and sensors."
                    }
                  />
                )}

                {!props.isCreatingNewAsset && !isMigrating && !lastConfigSuccessful && (
                  <Warning
                    message={
                      "This asset failed to update its monitoring device configuration settings on " +
                      `${formatDateLocal(lastConfigAttemptUtc)}. Once you press 'Save Changes' the configuration ` +
                      "will be retried. You will get a notification when the attempt is complete."
                    }
                  />
                )}

                <Error message={errorMessage} />
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter className={styles.footer}>
          <Fragment>
            <div className="me-auto">
              {!props.hideDelete &&
                !props.isCreatingNewAsset &&
                !isRented &&
                userHasPermission([[DELETE_ASSETS_PERMISSION]]) && (
                  <button
                    data-test="asset-wizard-delete-button"
                    className={`${styles.button} btn btn-danger me-2`}
                    type="button"
                    disabled={loading}
                    onClick={() => setShowConfirmDelete(true)}
                  >
                    Delete
                  </button>
                )}
              {activePageNumber > 1 && (
                <button
                  data-test="asset-previous-page-button"
                  className={`${styles.btn} btn btn-primary`}
                  type="button"
                  disabled={loading}
                  onClick={() => setActivePageNumber((prev) => prev - 1)}
                >
                  Previous Page
                </button>
              )}
            </div>
            {activePageNumber === breadcrumbs.length && dataCollectionId !== -1 ? (
              <Fragment>
                <button
                  data-test="asset-wizard-cancel-button"
                  className={`${styles.button} btn btn-secondary`}
                  type="button"
                  disabled={loading}
                  onClick={() => exitModal()}
                >
                  Cancel
                </button>
                {props.isCreatingNewAsset && userHasPermission([[CREATE_ASSETS_PERMISSION]]) && (
                  <button
                    data-test="asset-wizard-create-button"
                    className={`${styles.btn} btn btn-success`}
                    type="button"
                    disabled={loading}
                    onClick={() => saveChanges()}
                  >
                    Create Asset
                  </button>
                )}
                {!props.isCreatingNewAsset && userHasPermission([[UPDATE_ASSETS_PERMISSION]]) && (
                  <button
                    data-test="asset-wizard-save-button"
                    className={`${styles.btn} btn btn-success`}
                    type="button"
                    disabled={loading}
                    onClick={() => saveChanges()}
                  >
                    Save Changes
                  </button>
                )}
              </Fragment>
            ) : (
              <button
                data-test="asset-next-page-button"
                className={`${styles.btn} btn btn-primary`}
                type="button"
                disabled={loading || dataCollectionId === -1}
                onClick={() => setActivePageNumber((prev) => prev + 1)}
              >
                Next Page
              </button>
            )}
          </Fragment>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        showModal={showConfirmDelete}
        title={`Delete '${name}'?`}
        content={`Are you sure that you want to delete the asset '${name}'?`}
        yesText="Delete Asset"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmDelete(false)}
        onYes={() => void deleteAsset(props.assetId)}
        onNo={() => setShowConfirmDelete(false)}
      />

      <ConfirmModal
        showModal={props.showModal && (showConfirmTemplateConfig || showConfirmSensorConfig)}
        title="Update monitoring device?"
        content={
          "The attached monitoring device requires a configuration update and may also need activation " +
          "or firmware flashing. You will receive a notification upon completion of all updates."
        }
        yesText="Update Device and Save Asset"
        noText="Cancel"
        danger={false}
        onClose={() => {
          setShowConfirmTemplateConfig(false);
          setShowConfirmSensorConfig(false);
        }}
        onYes={() =>
          props.isCreatingNewAsset
            ? createAsset()
            : isRented
            ? updateAssetRental(nickname, assetMapIconId, props.assetId)
            : editAsset(showConfirmTemplateConfig, showConfirmSensorConfig, props.assetId)
        }
        onNo={() => {
          setShowConfirmTemplateConfig(false);
          setShowConfirmSensorConfig(false);
        }}
      />

      <SaveChangesModal
        showModal={showConfirmExit}
        title="Changes have not been saved!"
        content="Are you sure that you want to exit without saving your changes?"
        onClose={() => setShowConfirmExit(false)}
        onSave={() => void saveChanges()}
        onNoSave={() => discardChanges()}
      />
    </div>
  );
}

interface Props {
  assetId: number;
  showModal: boolean;
  isCreatingNewAsset: boolean;
  disableDataCollectionSelection: boolean;
  initialAsset?: AssetConfiguration;
  templates: Template[];
  assetMapIcons: AssetMapIcon[];
  deviceTypes: DeviceType[];
  monitoringDevices: MonitoringDevice[];
  gatewayAssets: Asset[];
  productTypes: ProductType[];
  productManufacturers: string[];
  controllers: Controller[];
  modbusSensors: ModbusSensor[];
  analogSensors: AnalogSensor[];
  digitalSensors: DigitalSensor[];
  runtimeServiceAttributeMap: RuntimeServiceAttributeMap;
  onClose: () => void;
  onAction: (action: Action) => void;
  hideDelete?: boolean;
}

interface Template {
  assetTemplateId: number;
  name: string;
  isImmutable: boolean;
}

interface AssetMapIcon {
  assetMapIconId: number;
  name: string;
  code: string;
}

interface DeviceType {
  deviceTypeId: number;
  model: string;
  name: string;
  maxAnalogInputCount: number;
  maxDigitalInputCount: number;
  maxBluetoothSensorCount: number;
  maxDigitalOutputCount: number;
  isGatewayEnabled: boolean;
}

interface MonitoringDevice {
  formId?: number;
  deviceId: number;
  deviceType: string;
  deviceIdentifier: string;
  attributes?: DeviceAttribute[];
  deviceLog?: DeviceLog;
}

interface DeviceAttribute {
  code: string;
  name: string;
}

type DeviceLog = {
  lastUpdated: DeviceLogUnit;
} & {
  [key: string]: DeviceLogUnit;
};

interface DeviceLogUnit {
  code: string;
  name: string;
  value: string | number | boolean;
  latitude?: number;
  longitude?: number;
  unitLong: string;
  unitShort: string;
  unitSymbol: string;
  icon: string;
  isHistorical: boolean;
  sensorConnected?: boolean;
  mostRecentUtc?: string;
}

interface Asset {
  assetId: number;
  name: string;
  nickname: string;
  isRented?: boolean;
  isMigrating?: boolean;
  isGateway: boolean;
  lastConfigSuccessful?: boolean;
}

interface ProductType {
  productTypeId: number;
  name: string;
}

interface Controller {
  controllerId: number;
  controllerModel: string | null;
  manufacturerCompanyCode: string | null;
  name: string;
}

interface ModbusSensor {
  modbusSensorId: number;
  name: string;
  maxCount: number;
}

interface AnalogSensor {
  analogSensorId: number;
  name: string;
  applications: AnalogApplication[];
}

interface AnalogApplication {
  attributeId: number;
  attributeConnectedId: number;
  name: string;
}

interface AnalogSensorMap {
  [key: string]: AnalogInputSelection;
}

interface AnalogInputSelection {
  analogSensorId: number;
  attributeCode: string;
  attributeConnectedCode: string;
  attributeModeCode: string;
  attributeMinCode: string;
  attributeSpanCode: string;
  mappedAttributeId: number;
  mappedAttributeConnectedId: number;
}

interface AnalogInputCard {
  formId: number;
  sensorNumber: number;
  analogSensorId: number | null;
  attributeId: number | null;
  attributeCode: string;
}

interface DigitalSensor {
  digitalSensorId: number;
  name: string;
  applications: DigitalApplications[];
  hasCounterSupport: boolean;
}

interface DigitalApplications {
  attributeStateId: number;
  attributeFlowId: number;
  attributeAccumulationId: number;
  reportingDescription: string;
}

interface DigitalSensorMap {
  [key: string]: DigitalInputSelection;
}

interface DigitalInputSelection {
  digitalSensorId: number;
  attributeStateCode: string;
  attributeFlowCode: string;
  attributeAccumulationCode: string;
  attributeModeCode: string;
  attributeKFactorCode: string;
  attributeOffsetCode: string;
  attributeDebounceCode: string;
  attributeUnitsPerPulseCode: string;
  mappedAttributeStateId: number;
  mappedAttributeFlowId: number;
  mappedAttributeAccumulationId: number;
  reportingDescription: string;
}

interface DigitalOutputSelection {
  position: number;
  operationLabel: string;
  trueLabel: string;
  falseLabel: string;
}

interface DigitalInputCard {
  formId: number;
  sensorNumber: number;
  digitalSensorId: number | null;
  attributeStateCode: string;
  reportingDescription: string;
}

interface DigitalOutputCard {
  formId: number;
  position: number;
  operationLabel: string;
  trueLabel: string;
  falseLabel: string;
}

type PageTitle = "Asset Details" | "Product" | "Modbus" | "Analog Input" | "Digital Input" | "Digital Output" | "Data";

interface AssetConfiguration {
  assetId: number;
  name: string;
  nickname: string;
  assetTemplateId: number | null;
  assetMapIconId: number | null;
  isGateway: boolean;
  gatewayAssetId: number | null;
  hideGatewayData: boolean;
  productType: string;
  productModel: string;
  productIdentifier: string;
  productManufacturer: string;
  controllerModel: string | null;
  controllerManufacturer: string | null;
  modbusSensorId: number | null;
  modbusSensorSlaveId: number | null;
  deviceType: string | null;
  deviceIdentifier: string | null;
  gpsLatitude: string;
  gpsLongitude: string;
  isRented: boolean;
  analogInputs: AnalogInputSelection[];
  digitalInputs: DigitalInputSelection[];
  digitalOutputs: DigitalOutputSelection[];
}

interface RuntimeServiceAttributeMap {
  [key: string]: RuntimeServiceAttribute;
}

interface RuntimeServiceAttribute {
  attributeId: number;
  name: string;
  code: string;
}

interface PostResponseBody {
  assetId: number;
}

interface PutResponseBody {
  message: string;
}

interface DeleteResponseBody {
  message: string;
}

interface GatewayConfigurationResponseBody {
  analogInputs: SensorInfoMap | null;
  modbusSensors: ModbusInfo | null;
  controller: ControllerInfo | null;
  digitalInputs: SensorInfoMap | null;
  digitalOutputs: SensorInfoMap | null;
  gatewayDeviceType: string;
}

interface SensorInfoMap {
  [sensorNumber: number]: SensorInfo;
}

interface SensorInfo {
  reporting: string;
  assetName: string;
}

interface ControllerInfo {
  controllerId: number;
  assetName: string;
}

interface ModbusInfo {
  modbusSensorId: number | null;
  sensor: ModbusSensorInfoMap | null;
}

interface ModbusSensorInfoMap {
  [sensorNumber: number]: ModbusSensorInfo;
}

interface ModbusSensorInfo {
  assetName: string;
}

interface AssetRequestBody {
  assetName: string;
  assetNickname: string;
  assetTemplateId: number;
  assetMapIconId: number | null;
  productType: string;
  productModel: string;
  productIdentifier: string;
  productManufacturer: string;
  controllerModel: string | null;
  controllerManufacturerCode: string | null;
  deviceType: string | null;
  deviceIdentifier: string | null;
  updateTemplate: boolean;
  updateSensors: boolean;
  analogSensors: AnalogInputSelection[];
  digitalSensors: DigitalInputSelection[];
  digitalOutputs: DigitalOutputSelection[];
  updateServiceHours?: boolean;
  serviceHoursAttributeCode: string | null;
  runtimeSinceLastServiced: number | null;
  gpsLatitude: number | null;
  gpsLongitude: number | null;
  isGateway: boolean;
  hideGatewayData: boolean;
  gatewayAssetId: number | null;
  modbusSensorId: number | null;
  modbusSensorSlaveId: number | null;
}

interface Action {
  type: string;
  payload: Payload;
}

interface Payload {
  assetId?: number;
  asset?: AssetConfiguration;
  nickname?: string;
}

type ModbusMedium = "Not used" | "Controller" | "Sensor";
